/**
 * Theme Service (CAND-14: Dark / Light Theme Toggle)
 *
 * Responsibilities:
 * 1. Manages theme preference persistence in localStorage.
 * 2. Resolves system preference via matchMedia API.
 * 3. Applies data-theme attribute and color-scheme to document root.
 * 4. Listens for live OS theme preference changes.
 */

import {
  ThemePreference,
  ResolvedTheme,
  THEME_STORAGE_KEY,
} from '../types';

/**
 * Retrieves the stored theme preference from localStorage.
 */
export function getStoredThemePreference(): ThemePreference {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return 'system';
    }
    const val = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (val === 'light' || val === 'dark' || val === 'system') {
      return val;
    }
  } catch {
    // Graceful fallback for restricted environments
  }
  return 'system';
}

/**
 * Persists the theme preference to localStorage.
 */
export function setStoredThemePreference(preference: ThemePreference): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    }
  } catch {
    // Ignore storage write errors (e.g. storage full / private mode)
  }
}

/**
 * Inspects system prefers-color-scheme query.
 */
export function getSystemTheme(): ResolvedTheme {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
}

/**
 * Resolves a ThemePreference into an active 'light' or 'dark' theme.
 */
export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') {
    return getSystemTheme();
  }
  return preference;
}

/**
 * Applies the resolved theme to documentElement attributes and styles.
 */
export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const resolved = resolveTheme(preference);

  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.setAttribute('data-theme', resolved);
    document.documentElement.style.colorScheme = resolved;
  }

  return resolved;
}

/**
 * Subscribes to system OS color scheme changes.
 */
export function subscribeToSystemTheme(
  callback: (theme: ResolvedTheme) => void
): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => {};
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const listener = (e: MediaQueryListEvent) => {
    callback(e.matches ? 'dark' : 'light');
  };

  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  } else if ('addListener' in mediaQuery) {
    // Fallback for older WebKit environments
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (mediaQuery as any).addListener(listener);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (mediaQuery as any).removeListener(listener);
    };
  }

  return () => {};
}
